import React, { useState, useEffect, useRef, FunctionComponent } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { usePost, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { TableCell } from '../../../../components/component-items/datatable'
import { Form, Col, Row } from 'react-bootstrap'
import { PackageType } from '../../../../enums'

const NewBadge = styled.span`
  margin-left: 12px;
  background: #5d78ff;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
`

function upsert(array: any[], newItem: any | any[]) {
  // Always work with an array of items, even if it's just one
  const items = Array.isArray(newItem) ? newItem : [newItem]
  const updatedArray = [...array]

  items.forEach((item) => {
    const i = updatedArray.findIndex((_item) => _item.p_id === item.p_id)
    if (i > -1) {
      updatedArray[i] = item
    } else {
      updatedArray.push(item)
    }
  })

  return updatedArray
}

type HandlingUnitCardProps = {
  handling_unit_number: number
  total: number
  id: number | string
  isNew?: boolean
  no_packaging?: boolean
  order_items: any[]
  packaging_items: any[]
  deleteHandlingUnit: (handling_unit_number: number, id: number) => void
  updateList: boolean
  setUpdateList: (updateList: boolean) => void
  setPackStatus: (pack_status: string) => void
  handlingUnits: any[]
  setHandlingUnits: React.Dispatch<React.SetStateAction<any[]>>
}

export const HandlingUnitCard: FunctionComponent<HandlingUnitCardProps> = ({
  handling_unit_number,
  total,
  id,
  no_packaging,
  isNew,
  order_items,
  packaging_items,
  deleteHandlingUnit,
  updateList,
  setUpdateList,
  setPackStatus,
  handlingUnits,
  setHandlingUnits,
}) => {
  const { id: order } = useParams<{ id: string }>()

  const {
    state: { csrf },
  } = globalState()

  const [validated, setValidated] = useState(false)
  const [invalidBarcode, setInvalidBarcode] = useState(false)
  const packDefault = {
    barcode: '',
    qty: '1',
  }
  const [packItem, setPackItem] = useState(packDefault)

  const [noOuterPackaging, setNoOuterPackaging] = useState(false)
  const [orderItems, setOrderItems] = useState<typeof order_items>([])
  const [packagingItems, setPackagingItems] = useState<typeof packaging_items>([])

  useEffect(() => {
    setOrderItems(order_items)
    setPackagingItems(packaging_items)
    setNoOuterPackaging(!!no_packaging)
  }, [order_items, packaging_items, no_packaging])

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    event.stopPropagation()
    const form = event.currentTarget

    setValidated(true)
    setInvalidBarcode(false)

    if (form.checkValidity() === false) {
      return
    }
    const resp = await usePost(
      '/api/shipment/pack_item/',
      { pk: order, ...packItem, handling_unit: handling_unit_number },
      csrf,
      false,
      true,
      true
    )
    if (!resp) {
      notify({ type: 'error', message: 'Failed to pack item. Please try again.' })
      return
    }
    if (resp.error) {
      const errorMessage =
        typeof resp.error === 'string' && resp.error.trim()
          ? resp.error
          : 'An unexpected error occurred. Please verify the barcode being scanned.'
      alert(errorMessage)
      setInvalidBarcode(true)
      return
    }

    // If this handling unit has a placeholder ID (not yet assigned by server), set the real ID
    if (!resp.error && typeof id === 'string') {
      setHandlingUnits((prev) =>
        prev.map((handlingUnit) =>
          handlingUnit.handling_unit_number === handling_unit_number
            ? { ...handlingUnit, id: resp.handling_unit_id }
            : handlingUnit
        )
      )
    }

    if (resp.type === 'product') {
      setOrderItems(upsert(orderItems, resp))
    } else {
      if (resp.type !== PackageType.PACKAGE_ACCESSORY) {
        setNoOuterPackaging(false)
      }
      setPackagingItems(upsert(packagingItems, resp.items ?? resp))
    }

    setValidated(false)
    setPackItem(packDefault)
    setPackStatus(resp.pack_status)
    setUpdateList(!updateList)

    // Refresh the handling unit from server to ensure full line-item data is shown
    try {
      const detailsResp = await fetch(`/api/order/${order}/pack_details/`)
      if (detailsResp.ok) {
        const details = await detailsResp.json()
        const updated = details.handling_units?.find((s: any) => s.handling_unit_number === handling_unit_number)
        if (updated) {
          setHandlingUnits((prev) => prev.map((h) => (h.handling_unit_number === handling_unit_number ? updated : h)))
          // Also update local state from the authoritative server payload
          setOrderItems(updated.order_items || [])
          setPackagingItems(updated.packaging_items || [])
        }
      } else {
        notify({ type: 'warning', message: 'Could not refresh handling unit data' })
      }
    } catch (err) {
      notify({ type: 'error', message: 'Error refreshing handling unit data' })
    }
  }

  const handleNoPackaging = async () => {
    const resp = await usePost(
      '/api/shipment/pack_item/',
      { pk: order, barcode: 'no_packaging', handling_unit: handling_unit_number },
      csrf,
      false
    )
    setPackStatus(resp.pack_status)
    // Remove all non-accessory packaging items
    setPackagingItems((prevItems) => prevItems.filter((item) => item.type === PackageType.PACKAGE_ACCESSORY))
    setNoOuterPackaging(true)
  }

  const inputElement = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (inputElement?.current) {
      inputElement.current.focus()
      inputElement.current.select()
    }
  }, [])

  const [highlight, setHighlight] = useState<boolean>(!!isNew)
  useEffect(() => {
    if (isNew) {
      setHighlight(true)
      const t = setTimeout(() => {
        setHighlight(false)
      }, 2200)
      return () => clearTimeout(t)
    }
  }, [isNew])

  return (
    <GlobalStyles.FullPageCard className={highlight ? 'new-handling-unit' : ''}>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            Handling Unit #{handling_unit_number}
            <small>
              {/** Show this HU's total packed quantity (sum of packed across products + packaging). */}
              {(orderItems
                ? orderItems.reduce((sum: number, it: any) => sum + (it.packed > 0 ? Number(it.packed) : 0), 0)
                : 0) +
                (packagingItems
                  ? packagingItems.reduce((sum: number, it: any) => sum + (it.packed > 0 ? Number(it.packed) : 0), 0)
                  : 0)}{' '}
              Items
            </small>
            {highlight && <NewBadge>NEW</NewBadge>}
          </h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          <GlobalStyles.Button
            id={'delete-handling-unit-' + handling_unit_number}
            className="secondary"
            onClick={() => deleteHandlingUnit(handling_unit_number, Number(id))}
          >
            Delete Handling Unit
          </GlobalStyles.Button>
          <GlobalStyles.Button
            id={'no-packaging-' + handling_unit_number}
            className="secondary"
            disabled={noOuterPackaging}
            onClick={() => handleNoPackaging()}
          >
            No Outer Packaging
          </GlobalStyles.Button>
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>
      <div style={{ minHeight: 200, padding: '1em' }}>
        <Form validated={validated} onSubmit={handleSubmit}>
          <Row style={{ padding: '.5em 1em 0' }}>
            <Form.Group as={Col} md="7" className="required">
              <Form.Control
                id={'handling-unit-' + handling_unit_number}
                type="text"
                placeholder="Scan Items"
                name="scan"
                value={packItem.barcode}
                onChange={(e) => setPackItem({ ...packItem, barcode: e.target.value })}
                ref={inputElement}
                isInvalid={invalidBarcode}
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a valid barcode</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" controlId="qty" className="required">
              <Form.Control
                type="number"
                placeholder="Quantity"
                name="qty"
                value={packItem.qty}
                onChange={(e) => setPackItem({ ...packItem, qty: e.target.value })}
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a quantity</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="2">
              <GlobalStyles.Button type="submit" style={{ margin: 0, width: '100%', height: 40 }}>
                Add
              </GlobalStyles.Button>
            </Form.Group>
          </Row>
        </Form>
        <Row>
          <Form.Group as={Col} md="7">
            <GlobalStyles.DataTable>
              <thead>
                <tr>
                  <th>
                    <span className="text"></span>
                  </th>
                  <th>
                    <span className="text">SKU &amp; Description</span>
                  </th>
                  <th>
                    <span className="text center">Ordered</span>
                  </th>
                  <th>
                    <span className="text center">Packed</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map(({ img, p_id, sku, description, ordered, packed }) =>
                  packed > 0 ? (
                    <GlobalStyles.TableRow key={p_id}>
                      <TableCell center>
                        <img src={img} style={{ height: 'auto', maxWidth: 100, maxHeight: 50 }} />
                      </TableCell>
                      <TableCell input={<Link to={`/product/variant/${p_id}/`}>{sku}</Link>}>
                        <div>{description}</div>
                      </TableCell>
                      <TableCell
                        center
                        input={
                          <StatusBoxStyled style={{ width: 75, margin: 'auto' }} className={'silver'}>
                            {ordered}
                          </StatusBoxStyled>
                        }
                      />
                      <TableCell
                        center
                        input={
                          <StatusBoxStyled style={{ width: 75, margin: 'auto' }} className={'silver'}>
                            {packed}
                          </StatusBoxStyled>
                        }
                      />
                    </GlobalStyles.TableRow>
                  ) : null
                )}
              </tbody>
            </GlobalStyles.DataTable>
          </Form.Group>
          <Form.Group as={Col} md="5">
            <GlobalStyles.DataTable>
              <thead>
                <tr>
                  <th>
                    <span className="text"></span>
                  </th>
                  <th>
                    <span className="text">Packaging</span>
                  </th>
                  <th>
                    <span className="text center">Packed</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {packagingItems.map(({ img, p_id, sku, description, packed }) =>
                  packed > 0 ? (
                    <GlobalStyles.TableRow key={p_id}>
                      <TableCell center>
                        <img src={img} style={{ height: 'auto', maxWidth: 100, maxHeight: 50 }} />
                      </TableCell>
                      <TableCell input={<Link to={`/product/variant/${p_id}/`}>{sku}</Link>}>
                        <div>{description}</div>
                      </TableCell>
                      <TableCell
                        center
                        input={
                          <StatusBoxStyled style={{ width: 75, margin: 'auto' }} className={'silver'}>
                            {packed}
                          </StatusBoxStyled>
                        }
                      />
                    </GlobalStyles.TableRow>
                  ) : null
                )}
                {noOuterPackaging && (
                  <GlobalStyles.TableRow>
                    <TableCell />
                    <TableCell input={'No Outer Packaging'} />
                    <TableCell />
                  </GlobalStyles.TableRow>
                )}
              </tbody>
            </GlobalStyles.DataTable>
          </Form.Group>
        </Row>
      </div>
    </GlobalStyles.FullPageCard>
  )
}
